import { socketAuthOptions, attachSocketAuthRecovery } from '../../config/socketAuth';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { fr, enGB, ar } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
    MessageSquare,
    Search,
    CheckCircle,
    Eye,
    MoreVertical,
    Ban,
    Trash2,
    AlertCircle,
    Lock
} from 'lucide-react';
import { Menu } from '@headlessui/react';
import { ChatInterface } from '../Chat/ChatInterface';
import { chatService } from '../../services/chatService';
import { InspectionDetail } from './InspectionDetail';
import { Inspection } from "../../types/inspection";
import { inspectionService } from "../../services/inspectionService";
import { API_URL } from '../../config/api.config';
import { useLanguage } from '../../hooks/useLanguage';

interface GarageResponse {
    garageId: string;
    status: string;
    notes?: string;
    responseDate: Date;
}

interface Discussion {
    inspectionId: string;
    garageId: string;
    clientId: string;
    conversationId: string;
    lastMessage: {
        content: string;
        timestamp: Date;
        sender: {
            id: string;
            type: string;
            name: string;
        }
    };
    unreadCount: number;
    clientName: string;
    vehicleInfo?: {
        brand: string;
        model: string;
        year: string;
    };
    statusInspection?: string;
    status?: string;
    garageResponses?: GarageResponse[];
}

const DiscussionActions = ({
                               discussion,
                               onAction,
                               isLocked
                           }: {
    discussion: Discussion;
    onAction: (action: string, discussionId: string, clientId: string) => void;
    isLocked: boolean;
}) => {
    const { t, isRTL } = useLanguage();

    return (
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {isLocked && (
                <span className="text-gray-500" title={t('messages.status.locked')}>
                    <Lock size={16} />
                </span>
            )}

            {discussion.unreadCount > 0 && (
                <button
                    onClick={() => onAction('markAsRead', discussion.inspectionId, discussion.clientId)}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-500 hover:text-indigo-600"
                    title={t('messages.actions.markAsRead')}
                >
                    <CheckCircle size={16} />
                </button>
            )}

            {!isLocked ? (
                <button
                    onClick={() => onAction('viewInspection', discussion.inspectionId, discussion.clientId)}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-500 hover:text-indigo-600"
                    title={t('messages.actions.viewInspection')}
                >
                    <Eye size={16} />
                </button>
            ) : (
                <button
                    onClick={() => alert(t('messages.actions.inspectionNotAvailable'))}
                    className="p-2 text-gray-300 cursor-not-allowed"
                    title={t('messages.actions.inspectionNotAvailable')}
                >
                    <Eye size={16} />
                </button>
            )}

            {!isLocked && (
                <Menu as="div" className="relative">
                    <Menu.Button className="p-2 hover:bg-gray-100 rounded-full">
                        <MoreVertical size={16} className="text-gray-500" />
                    </Menu.Button>

                    <Menu.Items className={`absolute ${isRTL ? 'right-0' : 'left-0'} mt-2 w-48 bg-white rounded-lg shadow-lg z-10 border`}>
                        <Menu.Item>
                            {({ active }: { active: boolean }) => (
                                <button
                                    className={`${active ? 'bg-gray-100' : ''} w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-sm text-red-600`}
                                    onClick={() => onAction('block', discussion.inspectionId, discussion.clientId)}
                                >
                                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <Ban size={16} />
                                        <span>{t('messages.actions.block')}</span>
                                    </div>
                                </button>
                            )}
                        </Menu.Item>

                        <Menu.Item>
                            {({ active }: { active: boolean }) => (
                                <button
                                    className={`${active ? 'bg-gray-100' : ''} w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-sm text-red-600`}
                                    onClick={() => onAction('delete', discussion.inspectionId, discussion.clientId)}
                                >
                                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <Trash2 size={16} />
                                        <span>{t('messages.actions.delete')}</span>
                                    </div>
                                </button>
                            )}
                        </Menu.Item>
                    </Menu.Items>
                </Menu>
            )}
        </div>
    );
};

export const Messages = () => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const [discussions, setDiscussions] = useState<Discussion[]>([]);
    const [selectedDiscussion, setSelectedDiscussion] = useState<string | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [selectedDiscussionData, setSelectedDiscussionData] = useState<Discussion | null>(null);
    const [inspectionsMap, setInspectionsMap] = useState<{[key: string]: Inspection}>({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [selectedInspectionForDetails, setSelectedInspectionForDetails] = useState<Inspection | null>(null);

    const refreshInProgress = useRef(false);
    const refreshTimer = useRef<NodeJS.Timeout | null>(null);

    const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id;
    const garageName = JSON.parse(localStorage.getItem('user') || '{}').garageName;

    // Obtenir la locale date-fns appropriée
    const getDateLocale = () => {
        switch(currentLanguage) {
            case 'ar':
                return ar;
            case 'en':
                return enGB;
            default:
                return fr;
        }
    };

    const loadDiscussions = useCallback(async () => {
        if (refreshInProgress.current) return;

        refreshInProgress.current = true;

        try {
            setLoading(prev => !selectedDiscussion && prev);
            const data = await chatService.getAllDiscussionsByGarageId();
            setDiscussions(data);

            const inspectionMap: {[key: string]: Inspection} = {};
            for (const discussion of data) {
                try {
                    const inspection = await inspectionService.getInspectionById(discussion.inspectionId, false);
                    inspectionMap[discussion.inspectionId] = inspection;
                } catch (error) {
                    console.error(`Erreur lors du chargement de l'inspection pour ${discussion.inspectionId}:`, error);
                }
            }
            setInspectionsMap(inspectionMap);

            if (selectedDiscussion && selectedClientId) {
                const updatedDiscussion = data.find(
                    (d: Discussion) => d.inspectionId === selectedDiscussion && d.clientId === selectedClientId
                );
                if (updatedDiscussion) {
                    setSelectedDiscussionData(updatedDiscussion);
                }
            }
        } catch (error) {
            console.error('Error loading discussions:', error);
        } finally {
            setLoading(false);

            if (refreshTimer.current) {
                clearTimeout(refreshTimer.current);
            }

            refreshTimer.current = setTimeout(() => {
                refreshInProgress.current = false;
            }, 1000);
        }
    }, [garageId, selectedDiscussion, selectedClientId]);

    useEffect(() => {
        loadDiscussions();

        const socket = io(API_URL, socketAuthOptions);
        attachSocketAuthRecovery(socket);

        socket.on('new_message', (data) => {
            if (!refreshInProgress.current) {
                loadDiscussions();
            }
        });

        socket.on('messages_read', (data) => {
            if (!refreshInProgress.current) {
                loadDiscussions();
            }
        });

        socket.on('inspection_status_changed', (data) => {
            inspectionService.getInspectionById(data.inspectionId, false)
                .then(inspection => {
                    setInspectionsMap((prev: {[key: string]: Inspection}) => ({
                        ...prev,
                        [data.inspectionId]: inspection
                    }));

                    if (!refreshInProgress.current) {
                        loadDiscussions();
                    }
                })
                .catch(error => {
                    console.error('Error fetching updated inspection:', error);
                });
        });

        return () => {
            socket.disconnect();
            if (refreshTimer.current) {
                clearTimeout(refreshTimer.current);
            }
        };
    }, [loadDiscussions]);

    const handleSelectDiscussion = (discussionId: string, clientId: string) => {
        setSelectedDiscussion(discussionId);
        setSelectedClientId(clientId);

        const discussionData = discussions.find(
            d => d.inspectionId === discussionId && d.clientId === clientId
        );

        if (discussionData) {
            setSelectedDiscussionData(discussionData);

            if (discussionData.unreadCount > 0) {
                chatService.markMessagesAsRead(discussionId, clientId)
                    .then(() => {
                        setDiscussions(prevDiscussions =>
                            prevDiscussions.map(d => {
                                if (d.inspectionId === discussionId && d.clientId === clientId) {
                                    return { ...d, unreadCount: 0 };
                                }
                                return d;
                            })
                        );

                        handleMessagesRead();
                    })
                    .catch(error => {
                        console.error('Erreur lors du marquage des messages comme lus:', error);
                    });
            }
        }
    };

    const handleMessagesRead = useCallback(() => {
        if (!refreshInProgress.current) {
            if (refreshTimer.current) {
                clearTimeout(refreshTimer.current);
            }

            refreshTimer.current = setTimeout(() => {
                loadDiscussions();
            }, 500);
        }
    }, [loadDiscussions]);

    const handleInspectionUpdated = useCallback(async (inspectionId: string) => {
        try {
            const inspection = await inspectionService.getInspectionById(inspectionId, false);

            setInspectionsMap(prev => ({
                ...prev,
                [inspectionId]: inspection
            }));

            if (!refreshInProgress.current) {
                loadDiscussions();
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'inspection:', error);
        }
    }, [loadDiscussions]);

    const handleDiscussionAction = async (action: string, discussionId: string, clientId: string) => {
        switch (action) {
            case 'markAsRead':
                try {
                    await chatService.markMessagesAsRead(discussionId, clientId);

                    setTimeout(() => {
                        if (!refreshInProgress.current) {
                            loadDiscussions();
                        }
                    }, 500);
                } catch (error) {
                    console.error('Error marking as read:', error);
                }
                break;

            case 'viewInspection':
                try {
                    const inspection = await inspectionService.getInspectionById(discussionId, false);
                    const garageResponse = inspection.garageResponses && inspection.garageResponses.find(
                        (response: GarageResponse) => response.garageId === garageId
                    );
                    setSelectedInspectionForDetails(inspection);
                } catch (error) {
                    console.error('Error fetching inspection:', error);
                }
                break;

            case 'block':
                if (window.confirm(t('messages.actions.confirmBlock'))) {
                    try {
                        // await chatService.blockDiscussion(discussionId);
                        loadDiscussions();
                    } catch (error) {
                        console.error('Error blocking discussion:', error);
                    }
                }
                break;

            case 'delete':
                if (window.confirm(t('messages.actions.confirmDelete'))) {
                    try {
                        // await chatService.deleteDiscussion(discussionId);
                        loadDiscussions();
                    } catch (error) {
                        console.error('Error deleting discussion:', error);
                    }
                }
                break;
        }
    };

    const isDiscussionLocked = (discussionId: string) => {
        const inspection = inspectionsMap[discussionId];
        if (!inspection) return false;

        if (inspection.status === 'completed' || inspection.status === 'rejected') return true;

        if (inspection.quotes && inspection.quotes.length > 0) {
            const hasAcceptedQuoteFromOtherGarage = inspection.quotes.some(
                (quote) => (quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment') && quote.garageId !== garageId
            );
            if (hasAcceptedQuoteFromOtherGarage) return true;
        }

        if (inspection.garageResponses && inspection.garageResponses.length > 0) {
            const hasRejectedResponse = inspection.garageResponses.some(
                (response) => response.garageId === garageId && response.status === 'rejected'
            );
            if (hasRejectedResponse) return true;
        }

        return false;
    };

    const filteredDiscussions = discussions.filter(discussion => {
        const inspection = inspectionsMap[discussion.inspectionId];

        const vehicleInfo = inspection
            ? `${inspection.vehicleBrand} ${inspection.vehicleModel} ${inspection.vehicleYear}`
            : "";

        const lastMessageContent = discussion.lastMessage.content || "";

        const searchString = `${vehicleInfo} ${discussion.clientName} ${lastMessageContent}`.toLowerCase();

        return searchString.includes(searchTerm.toLowerCase());
    });

    const sortedDiscussions = [...filteredDiscussions].sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;

        return new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime();
    });

    if (loading && !selectedDiscussion) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className={`h-full flex ${isRTL ? 'flex-row-reverse' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Liste des discussions */}
            <div className={`w-1/3 ${isRTL ? 'border-l' : 'border-r'} bg-white`}>
                <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold mb-4">{t('messages.title')}</h2>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={t('messages.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${isRTL ? 'text-right' : 'text-left'}`}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 h-5 w-5 text-gray-400`} />
                    </div>
                </div>

                <div className="overflow-y-auto h-[calc(100vh-200px)]">
                    {sortedDiscussions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <MessageSquare className="h-12 w-12 mb-2" />
                            <p>{t('messages.noDiscussions')}</p>
                        </div>
                    ) : (
                        sortedDiscussions.map((discussion) => {
                            const hasUnread = discussion.unreadCount > 0;
                            const isLocked = isDiscussionLocked(discussion.inspectionId);

                            const inspection = inspectionsMap[discussion.inspectionId];
                            const vehicleInfo = inspection
                                ? `${inspection.vehicleBrand} ${inspection.vehicleModel} (${inspection.vehicleYear})`
                                : t('messages.vehicle.unknown');

                            return (
                                <div
                                    key={`${discussion.inspectionId}-${discussion.clientId}`}
                                    className={`p-4 border-b hover:bg-gray-50 cursor-pointer 
                                        ${selectedDiscussion === discussion.inspectionId && selectedClientId === discussion.clientId ? 'bg-indigo-50' : ''}
                                        ${hasUnread ? `bg-indigo-50 ${isRTL ? 'border-r-4 border-r-indigo-600' : 'border-l-4 border-l-indigo-600'}` : ''}
                                        ${isLocked ? 'opacity-75' : ''}`}
                                >
                                    <div className={`flex justify-between items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <div
                                            className="flex-1"
                                            onClick={() => handleSelectDiscussion(discussion.inspectionId, discussion.clientId)}
                                        >
                                            <div className={`flex justify-between items-start mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <h3 className={`font-medium ${hasUnread ? 'text-indigo-800 font-semibold' : ''} ${isRTL ? 'text-right' : 'text-left'}`}>
                                                    {vehicleInfo}
                                                    {isLocked && (
                                                        <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded`}>
                                                            {t('messages.status.locked')}
                                                        </span>
                                                    )}
                                                </h3>
                                                <span className={`text-xs ${hasUnread ? 'text-indigo-700 font-semibold' : 'text-gray-500'}`}>
                                                    {format(new Date(discussion.lastMessage.timestamp), 'HH:mm', { locale: getDateLocale() })}
                                                </span>
                                            </div>
                                            <p className={`text-sm truncate ${hasUnread ? 'text-indigo-800 font-medium' : 'text-gray-500'} ${isRTL ? 'text-right' : 'text-left'}`}>
                                                {discussion.lastMessage.content.substring(0, 50)}...
                                            </p>
                                        </div>

                                        <DiscussionActions
                                            discussion={discussion}
                                            onAction={handleDiscussionAction}
                                            isLocked={isLocked}
                                        />
                                    </div>

                                    {hasUnread && (
                                        <div className={`flex justify-between items-center mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <AlertCircle size={16} className={`text-indigo-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                                <span className="text-xs text-indigo-700">{t('messages.unread.newMessages')}</span>
                                            </div>
                                            <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                                                {discussion.unreadCount}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Interface de chat */}
            <div className="flex-1">
                {selectedDiscussion && selectedClientId ? (
                    <ChatInterface
                        inspectionId={selectedDiscussion}
                        garageId={garageId}
                        garageName={garageName}
                        onMessagesRead={handleMessagesRead}
                        inspectionStatus={inspectionsMap[selectedDiscussion]?.status}
                        isDisabled={isDiscussionLocked(selectedDiscussion)}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <MessageSquare className="h-16 w-16 mb-4" />
                        <p>{t('messages.selectDiscussion')}</p>
                    </div>
                )}
            </div>

            {selectedInspectionForDetails && (
                <InspectionDetail
                    inspection={selectedInspectionForDetails}
                    onClose={() => setSelectedInspectionForDetails(null)}
                    onStartChat={() => {
                        const clientId = selectedInspectionForDetails.clientId;
                        handleSelectDiscussion(selectedInspectionForDetails._id, clientId);
                        setSelectedInspectionForDetails(null);
                    }}
                    onCreateQuote={() => console.log('Create quote')}
                    onStatusChange={async (id, status) => {
                        await inspectionService.updateInspectionStatus(id, status);
                        await handleInspectionUpdated(id);
                        handleMessagesRead();
                    }}
                    onInspectionUpdated={handleInspectionUpdated}
                />
            )}
        </div>
    );
};